/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import { Footer } from '@components/systems'
import { LinearProgress } from '@mui/material'

import { paymentPageStyle } from '@components/systems/monolith/payment/payment.style'
import { MonolithPaymentItems } from '@components/systems/monolith/payment/PaymentItems'
import { MonolithPaymentCustomerInfo } from '@components/systems/monolith/payment/PaymentCustomerInfo'
import { MonolithPaymentDelivery } from '@components/systems/monolith/payment/PaymentDelivery'
import { MonolithPaymentCard } from '@components/systems/monolith/payment/PaymentCard'
import { MonolithPaymentTotalCost } from '@components/systems/monolith/payment/PaymentTotalCost'
import { MonolithPaypalPayment } from '@components/systems/monolith/payment/PaypalPayment'
import { MonolithCardPaymentButton } from '@components/systems/monolith/payment/CardPaymentButton'
import { PriceChangeModal } from '@components/systems/monolith/payment/PriceChangeModal'

import {
	useAppSelector,
	useAppDispatch,
	paymentActions,
	ApiGetDataStatus,
	PaymentDeliveryType,
	CheckoutQuoteProblemType,
	PaymentStateType,
} from '@redux/index'
import { CartQuote } from '@utils'
import { unsupportedPayPalCountries } from '@redux/store/home/payment.store'
import { useNavigate } from 'react-router-dom'

/**
 * Payment — Monolith(2안). 구 페이지는 `pages/home/Payment.page.tsx` 에 보관.
 *
 * 결제 흐름(수단 선택 → 고객 → 배송지 → 카드 → 금액 → 실행)과 결과 모달은 구 페이지 그대로다.
 * 블록 컴포넌트도 로직은 옮기기만 했다 — `systems/monolith/payment/` 의 일곱 파일이 구
 * `systems/payment/` 의 짝이고, 구 파일은 보관만 한다.
 *
 * 외형은 DESIGN_SYSTEM §4 4:8 밴드 — 좌측 키커, 우측 내용. 블랙 CTA 는 마지막 하나뿐이다(§5).
 */
const MonolithPaymentPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const status: ApiGetDataStatus = useAppSelector(state => state.payment.paymentStatus)
	const showModal: boolean = useAppSelector(state => state.payment.showModal)
	const modalInfo: ModalInfoType = useAppSelector(state => state.payment.modalInfo)

	const paymentMethod: string = useAppSelector(state => state.payment.paymentMethod)
	const delivery: PaymentDeliveryType = useAppSelector(
		state => state.payment.paymentDelivery,
	)
	// redux-persist 가 되살린 옛 상태에는 이 키들이 없을 수 있다 — 빈 값으로 받는다
	const quoteProblems: CheckoutQuoteProblemType[] =
		useAppSelector(state => state.payment.quoteProblems) ?? []
	const priceChanges: PaymentStateType['priceChanges'] = useAppSelector(
		state => state.payment.priceChanges,
	) ?? { currency: 'USD', increased: [], decreased: [] }

	const isKrwPayment: boolean = React.useMemo(
		() => delivery.countryIsoCode === 'KR',
		[delivery],
	)

	/*
		한국 배송은 카드 한 길뿐이다 — PayPal 은 KR 을 받지 않는다(`unsupportedPayPalCountries`).
		고를 것이 하나뿐인 자리에 선택을 세우지 않는다: Method 블록을 접고 카드로 정해 둔다.
		배송국을 한국 밖으로 바꾸면 블록이 다시 서고, 카드가 고른 상태로 남는다.
	*/
	React.useEffect(() => {
		if (isKrwPayment && paymentMethod !== 'credit') {
			reduxDispatch(paymentActions.PAYMENT_METHOD('credit'))
		}
	}, [isKrwPayment, paymentMethod, reduxDispatch])

	const onModalSubmit = () => {}

	const onPaymentMethodClick = (paymentType: string) => {
		if (
			paymentType === 'paypal' &&
			unsupportedPayPalCountries.includes(delivery.countryIsoCode)
		) {
			reduxDispatch(
				paymentActions.MODAL_INFO({
					target: 'paypalNotAllowed',
					title: 'PayPal Unavailable',
				}),
			)
			reduxDispatch(paymentActions.SHOW_MODAL(true))
		} else {
			reduxDispatch(paymentActions.PAYMENT_METHOD(paymentType))
		}
	}

	const methodList: { value: string; label: string }[] = [
		{ value: 'paypal', label: 'PayPal' },
		{ value: 'credit', label: 'Credit / Debit card' },
	]

	return (
		<View css={style}>
			<View className="mnWrap">
				<View className="mnPayHeader">
					<h1 className="mnPayTitle">Payment</h1>
					<p className="mnPayLede">The amount charged, and the address it ships to.</p>
				</View>

				{['init', 'loading'].includes(status) ? (
					<View className="mnPayLoading">
						<LinearProgress className="mnPayLoadingBar" />
					</View>
				) : (
					<>
						<MonolithPaymentItems />

						{!isKrwPayment && (
							<View className="mnPaySection">
								<View className="mnPaySectionHead">
									<span className="mnKicker">Method</span>
								</View>
								<View className="mnPaySectionBody">
									<View
										className="mnPayChips"
										role="radiogroup"
										aria-label="Payment method"
									>
										{methodList.map(aMethod => (
											<button
												key={aMethod.value}
												type="button"
												role="radio"
												aria-checked={paymentMethod === aMethod.value}
												className={`mnPayChip ${
													paymentMethod === aMethod.value ? 'on' : ''
												}`}
												onClick={() => onPaymentMethodClick(aMethod.value)}
											>
												{aMethod.label}
											</button>
										))}
									</View>
								</View>
							</View>
						)}

						{paymentMethod !== '' && (
							<>
								<MonolithPaymentCustomerInfo />
								<MonolithPaymentDelivery />

								{!isKrwPayment && paymentMethod === 'credit' && <MonolithPaymentCard />}

								<MonolithPaymentTotalCost />

								<View className="mnPaySection">
									<View className="mnPaySectionHead">
										<span className="mnKicker">Confirm</span>
									</View>
									<View className="mnPaySectionBody">
										{paymentMethod === 'paypal' && <MonolithPaypalPayment />}
										{paymentMethod === 'credit' && <MonolithCardPaymentButton />}
									</View>
								</View>
							</>
						)}
					</>
				)}
			</View>

			<Footer />

			<PriceChangeModal
				show={showModal && modalInfo.target === 'priceChanged'}
				changes={priceChanges}
				onConfirm={keepIds => {
					reduxDispatch(paymentActions.SHOW_MODAL(false))
					reduxDispatch(paymentActions.PRICE_DECISION(keepIds))
				}}
				onCancel={() => {
					reduxDispatch(paymentActions.SHOW_MODAL(false))
					if (priceChanges.increased.length > 0) {
						navigate('/cart')
					}
				}}
			/>

			<Modal
				target={modalInfo.target ?? ''}
				title={modalInfo.title}
				show={showModal && modalInfo.target !== 'priceChanged'}
				onCloseModal={() => {
					if (['serverError'].includes(modalInfo.target)) {
						navigate(-1)
					} else if (['paymentSuccess'].includes(modalInfo.target)) {
						navigate('/')
					}

					reduxDispatch(paymentActions.SHOW_MODAL(false))
				}}
				showSubmit={false}
				closeStatus="inherit"
				closeTitle={['serverError'].includes(modalInfo.target) ? 'Go Back' : 'Close'}
				submitTitle="Get Code"
				isTitleDanger={[
					'serverError',
					'unexpectedError',
					'dataError',
					'invalidateDataError',
					'paymentError',
					'quoteChanged',
				].includes(modalInfo.target)}
				onSubmit={onModalSubmit}
			>
				{modalInfo.target === 'serverError' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">An server error has occurred.</Text>
						<Text category="p2">The server is not responding.</Text>
						<Text category="p2">Please review your entries and try again.</Text>
					</View>
				) : modalInfo.target === 'unexpectedError' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">An unexpected error has occurred.</Text>
						<Text category="p2">The requested data does not exist.</Text>
						<Text category="p2">Please review your entries and try again.</Text>
					</View>
				) : modalInfo.target === 'dataError' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">An error occurred in the data.</Text>
						<Text category="p2">
							The database and the requested data are not the same.
						</Text>
						<Text category="p2">Please review your entries and try again.</Text>
					</View>
				) : modalInfo.target === 'paymentError' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">There was an error processing the payment.</Text>
						<Text category="p2">Please check if any required fields are missing</Text>
						<Text category="p2">or incorrectly formatted.</Text>
						<Text category="p2">Review your information and try again.</Text>
					</View>
				) : modalInfo.target === 'quoteChanged' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">The cart no longer matches what is on sale.</Text>
						{quoteProblems.map((aProblem, index) => (
							<Text key={`${aProblem.code}-${index}`} category="p2">
								{`• ${CartQuote.describe(aProblem, isKrwPayment ? 'KRW' : 'USD')}`}
							</Text>
						))}
						<Text category="p2">The amounts below were updated.</Text>
					</View>
				) : modalInfo.target === 'paymentSuccess' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">Your payment was successfully processed.</Text>
						<Text category="p2">Thank you for your purchase!</Text>
						<Text category="p2">A confirmation email has been sent to you.</Text>
					</View>
				) : modalInfo.target === 'paypalNotAllowed' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">Sorry, PayPal is not available in your country.</Text>
						<Text category="p2">Please use a Credit or Debit card instead.</Text>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

export default MonolithPaymentPage

const style = css({
	...paymentPageStyle,
})
